import { ApiError } from "@/lib/api/error-handler";
import { persianToEnglishNumber } from "@/utils/modules/numbers";
import type { IApiEnvelopeError } from "@/types/common";
import type {
  IBillingUser,
  IDiscountCode,
  PaymentMethod,
  WalletEntryStatus,
  WalletEntryType,
} from "@/types/billing";

export type DiscountCheckStatus =
  | { status: "idle" }
  | { status: "checking"; code: string }
  | { status: "valid"; discount: IDiscountCode }
  | {
      status: "invalid" | "expired" | "used" | "min-amount";
      message: string;
      messageKey?: string;
    };

export interface QuotePreviewTotals {
  conversations: number;
  base: number;
  discountAmount: number;
  giftBonus: number;
  taxRate: number;
  tax: number;
  subtotal: number;
  walletApplied: number;
  payable: number;
}

export interface WalletTopupTotals {
  base: number;
  taxRate: number;
  tax: number;
  total: number;
}

export function walletTopupTotals(amount: number, taxRate: number): WalletTopupTotals {
  const base = Math.max(0, amount);
  const tax = Math.round(base * taxRate);
  return { base, taxRate, tax, total: base + tax };
}

export function extractApiError(
  error: unknown,
): { code: string; message: string; data?: Record<string, unknown> } | null {
  if (error instanceof ApiError) {
    if (!error.code) return null;
    return { code: error.code, message: error.message ?? "", data: error.data ?? undefined };
  }
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response: { data?: { error?: IApiEnvelopeError } } }).response;
    const err = response.data?.error;
    if (!err?.code) return null;
    return { code: err.code, message: err.message ?? "", data: err.data ?? undefined };
  }
  return null;
}

export function discountCheckStatusForError(
  code: string | null,
  fallback: string,
  serverMessage?: string | null,
): Extract<DiscountCheckStatus, { status: "invalid" | "expired" | "used" | "min-amount" }> {
  const message = serverMessage?.trim() || undefined;
  switch (code) {
    case "DISCOUNT_EXPIRED":
      return {
        status: "expired",
        message: message ?? "مهلت استفاده از این کد تخفیف تمام شده است.",
        messageKey: "expired",
      };
    case "DISCOUNT_EXHAUSTED":
      return {
        status: "used",
        message: message ?? "ظرفیت استفاده از این کد تخفیف تکمیل شده است.",
        messageKey: "exhausted",
      };
    case "DISCOUNT_ALREADY_USED":
      return {
        status: "used",
        message: message ?? "شما قبلاً از این کد تخفیف استفاده کرده‌اید.",
        messageKey: "alreadyUsed",
      };
    case "DISCOUNT_NOT_STARTED":
      return {
        status: "invalid",
        message: message ?? "این کد تخفیف هنوز فعال نشده است.",
        messageKey: "notStarted",
      };
    case "DISCOUNT_DISABLED":
      return {
        status: "invalid",
        message: message ?? "این کد تخفیف غیرفعال است.",
        messageKey: "disabled",
      };
    case "DISCOUNT_NOT_APPLICABLE":
      return {
        status: "invalid",
        message: message ?? "این کد تخفیف برای این خرید قابل استفاده نیست.",
        messageKey: "notApplicable",
      };
    case "DISCOUNT_MIN_ORDER":
      return {
        status: "min-amount",
        message: message ?? "مبلغ سفارش کمتر از حداقل مبلغ این کد تخفیف است.",
        messageKey: "minOrder",
      };
    default:
      return { status: "invalid", message: message ?? fallback };
  }
}

export function packageBonusOf(pkg: { conversations: number; bonus_pct: number }): number {
  return Math.round((pkg.conversations * pkg.bonus_pct) / 100);
}

export function requiredExtraConversations(conversations: number, planLimit: number): number {
  return Math.max(0, conversations - planLimit);
}

export function billingUserDisplayName(user: IBillingUser | null | undefined): string {
  if (!user) return "—";
  return (
    [user.first_name, user.last_name].filter(Boolean).join(" ") || user.name || user.mobile || "—"
  );
}

export function walletEntryIsInflow(type: WalletEntryType): boolean {
  return type === "credit" || type === "bonus";
}

export function walletEntryRefundedAmount(entry: { refunded_amount?: number | null }): number {
  return entry.refunded_amount ?? 0;
}

export function walletEntryRefundableRemaining(entry: {
  amount: number;
  refunded_amount?: number | null;
}): number {
  return Math.max(0, Math.abs(entry.amount) - walletEntryRefundedAmount(entry));
}

export function walletEntryIsRefundable(entry: {
  amount: number;
  type: WalletEntryType;
  status: WalletEntryStatus;
  refunded_amount?: number | null;
}): boolean {
  return (
    entry.amount < 0 &&
    entry.status === "completed" &&
    (entry.type === "purchase" || entry.type === "debit") &&
    walletEntryRefundableRemaining(entry) > 0
  );
}

export function orderPaymentMethod(order: {
  wallet_part?: number | null;
  gateway_part?: number | null;
}): PaymentMethod | null {
  const wallet = (order.wallet_part ?? 0) > 0;
  const gateway = (order.gateway_part ?? 0) > 0;
  if (wallet && gateway) return "both";
  if (wallet) return "wallet";
  if (gateway) return "gateway";
  return null;
}

export function quotePreviewTotals(input: {
  conversations: number;
  perConversationPrice: number;
  discount: DiscountCheckStatus;
  useWallet: boolean;
  walletBalance: number;
  taxRate: number;
}): QuotePreviewTotals {
  const { conversations, perConversationPrice, discount, useWallet, walletBalance, taxRate } =
    input;
  const base = conversations * perConversationPrice;
  let discountAmount = 0;
  let giftBonus = 0;
  if (discount.status === "valid") {
    const d = discount.discount;
    if (d.min_order_amount === null || base >= d.min_order_amount) {
      if (d.kind === "percent") {
        discountAmount = Math.min(
          Math.round((base * d.value) / 100),
          d.max_discount ?? Number.MAX_SAFE_INTEGER,
        );
      } else if (d.kind === "amount") {
        discountAmount = Math.min(d.value, base);
      } else {
        giftBonus = d.value;
      }
    }
  }
  const taxable = Math.max(0, base - discountAmount);
  const tax = Math.round(taxable * taxRate);
  const subtotal = taxable + tax;
  const walletApplied = useWallet ? Math.min(walletBalance, subtotal) : 0;
  return {
    conversations,
    base,
    discountAmount,
    giftBonus,
    taxRate,
    tax,
    subtotal,
    walletApplied,
    payable: Math.max(0, subtotal - walletApplied),
  };
}

type AnnualDiscountCandidate = {
  is_trial?: boolean | null;
  monthly: number;
  yearly: number;
};

export function annualPlanDiscountPercent(plan: AnnualDiscountCandidate): number {
  if (plan.is_trial || plan.monthly <= 0 || plan.yearly <= 0) return 0;
  const monthlyTotal = plan.monthly * 12;
  if (plan.yearly >= monthlyTotal) return 0;
  return Math.round(((monthlyTotal - plan.yearly) / monthlyTotal) * 10000) / 100;
}

export function sharedAnnualDiscountPercent(plans: AnnualDiscountCandidate[]): number | null {
  let shared: number | null = null;
  for (const plan of plans) {
    if (plan.is_trial || plan.monthly <= 0 || plan.yearly <= 0) continue;
    const pct = annualPlanDiscountPercent(plan);
    if (pct <= 0) return null;
    if (shared === null) {
      shared = pct;
    } else if (shared !== pct) {
      return null;
    }
  }
  return shared;
}

export function normalizeNationalIdInput(value: string): string {
  return persianToEnglishNumber(value).replace(/[^0-9]/g, "");
}

const LEGAL_NATIONAL_ID_WEIGHTS = [29, 27, 23, 19, 17, 29, 27, 23, 19, 17];

function isValidIranianNationalCode(value: string): boolean {
  const digits = value.split("").map(Number);
  const control = digits[9];
  const sum = digits
    .slice(0, 9)
    .reduce((acc, digit, index) => acc + digit * (10 - index), 0);
  const remainder = sum % 11;
  return remainder < 2 ? control === remainder : control === 11 - remainder;
}

function isValidIranianLegalNationalId(value: string): boolean {
  const digits = value.split("").map(Number);
  const sum = digits
    .slice(0, 10)
    .reduce(
      (acc, digit, index) => acc + (digit + 2 + digits[9]) * LEGAL_NATIONAL_ID_WEIGHTS[index],
      0,
    );
  const remainder = sum % 11;
  return (remainder === 10 ? 0 : remainder) === digits[10];
}

export type QuoteInvoiceFieldErrors = {
  company?: string;
  nationalId?: string;
  address?: string;
};

export function validateQuoteInvoiceFields(fields: {
  company: string;
  nationalId: string;
  address: string;
}): QuoteInvoiceFieldErrors {
  const errors: QuoteInvoiceFieldErrors = {};

  const company = fields.company.trim();
  if (company === "") {
    errors.company = "این فیلد الزامی است.";
  } else if (company.length <= 3) {
    errors.company = "نام باید بیشتر از ۳ حرف باشد.";
  }

  const rawNationalId = fields.nationalId.trim();
  if (rawNationalId === "") {
    errors.nationalId = "این فیلد الزامی است.";
  } else {
    const nationalId = normalizeNationalIdInput(rawNationalId);
    if (nationalId === "" || rawNationalId !== nationalId) {
      errors.nationalId = "فقط عدد وارد کنید.";
    } else if (nationalId.length !== 10 && nationalId.length !== 11) {
      errors.nationalId = "کد ملی ۱۰ رقم و شناسه ملی ۱۱ رقم است.";
    } else if (/^(\d)\1+$/.test(nationalId)) {
      errors.nationalId = "شماره تکراری معتبر نیست.";
    } else if (
      nationalId.length === 10
        ? !isValidIranianNationalCode(nationalId)
        : !isValidIranianLegalNationalId(nationalId)
    ) {
      errors.nationalId = "شماره واردشده معتبر نیست.";
    }
  }

  const address = fields.address.trim();
  if (address === "") {
    errors.address = "این فیلد الزامی است.";
  } else if (address.length <= 3) {
    errors.address = "نشانی باید بیشتر از ۳ حرف باشد.";
  }

  return errors;
}
